import React, { useState, useEffect, useRef } from "react";

import Footer from "../components/Footer";
import GreyGridBackground from "../components/backgroundWhite";
import { FaBars, FaTimes } from "react-icons/fa";

// Only asset used on this page — everything else is drawn inline.
import CleverPeLogo from "../assets/logo/CleverPe_Black_H.svg";
import verifyMobileImg from "../assets/native/login.jpeg";
import selectEmiImg from "../assets/native/emi.jpeg";
import approvalImg from "../assets/native/approval.png";
import loanJourneyImg from "../assets/native/dashbaord.jpeg";
import partnerLogo1 from "../assets/healthpartners/cosmo-logo.avif";
import partnerLogo2 from "../assets/healthpartners/i-care-logo.png";
import partnerLogo3 from "../assets/healthpartners/logo-prime.png";


type IconProps = { className?: string };



const IconBolt: React.FC<IconProps> = ({ className = "w-6 h-6" }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
        strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8Z" />
    </svg>
);

const IconWorkflow: React.FC<IconProps> = ({ className = "w-6 h-6" }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
        strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M9 2v4M15 2v4" />
        <path d="M6 6h12v4a6 6 0 0 1-12 0V6Z" />
        <path d="M12 16v6" />
    </svg>
);

const IconReceipt: React.FC<IconProps> = ({ className = "w-6 h-6" }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
        strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M5 3v18l2.5-1.5L10 21l2-1.5L14 21l2.5-1.5L19 21V3H5Z" />
        <path d="M8.5 8h7M8.5 12h7M8.5 16h4" />
    </svg>
);

/* =========================================================
   PAGE
========================================================= */

const TENURES = [3, 6, 9, 12, 18, 24];

// Replace the STEPS array — accent per-step is gone, one consistent brand color now
const STEPS = [
    {
        title: "Verify mobile number",
        desc: "Enter the OTP received",
        img: verifyMobileImg,
    },
    {
        title: "Get instant approval",
        desc: "Eligibility checked in minutes",
        img: approvalImg,
    },
    {
        title: "Select EMI",
        desc: "Choose a plan that fits",
        img: selectEmiImg,
    },
    {
        title: "Complete digital journey",
        desc: "Complete your journey and that's it!",
        img: loanJourneyImg,
    },
];

// Remove the old ACCENTS object entirely — no longer needed
const STEP_DURATION = 4000; // ms

const HealthFinancing: React.FC = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    // Interactive bill-to-EMI splitter
    const [amount, setAmount] = useState(180000);
    const [tenure, setTenure] = useState(12);
    const emi = Math.round(amount / tenure);

    // Interactive journey stepper
    // Interactive journey stepper
    const [active, setActive] = useState(0);
    const [progress, setProgress] = useState(0);
    const [hasCompleted, setHasCompleted] = useState(false);
    const [inView, setInView] = useState(false);
    const rafRef = useRef<number | null>(null);
    const startRef = useRef<number | null>(null);
    const sectionRef = useRef<HTMLElement | null>(null);
    const wasInViewRef = useRef(false);

    // Watch the section — play once on entry, reset on re-entry after leaving
    useEffect(() => {
        const el = sectionRef.current;
        if (!el) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                const nowInView = entry.isIntersecting;
                // Only reset if it had actually left view before (not on first mount)
                if (nowInView && !wasInViewRef.current) {
                    setActive(0);
                    setProgress(0);
                    setHasCompleted(false);
                    startRef.current = null;
                }
                wasInViewRef.current = nowInView;
                setInView(nowInView);
            },
            { threshold: 0.4 }
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    // Drive the tracker — only while in view and not yet completed a full pass
    useEffect(() => {
        if (!inView || hasCompleted) {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            return;
        }

        startRef.current = performance.now() - progress * STEP_DURATION;

        const tick = (now: number) => {
            const elapsed = now - (startRef.current ?? now);
            const pct = Math.min(elapsed / STEP_DURATION, 1);
            setProgress(pct);
            if (pct >= 1) {
                if (active === STEPS.length - 1) {
                    // finished the last step — stop here, don't loop back to 0
                    setProgress(1);
                    setHasCompleted(true);
                    return;
                }
                setActive((a) => a + 1);
                setProgress(0);
                startRef.current = now;
            }
            rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inView, hasCompleted, active]);

    const goTo = (i: number) => {
        setActive(i);
        setProgress(0);
        setHasCompleted(i === STEPS.length - 1);
    };

    // Approval tracker — plays once on scroll into view, replays if it leaves and re-enters
    const [approvalDone, setApprovalDone] = useState(false);
    const [approvalKey, setApprovalKey] = useState(0);
    const approvalCardRef = useRef<HTMLDivElement | null>(null);
    const approvalWasInViewRef = useRef(false);

    useEffect(() => {
        const el = approvalCardRef.current;
        if (!el) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                const nowInView = entry.isIntersecting;

                if (nowInView && !approvalWasInViewRef.current) {
                    setApprovalDone(false);
                    setApprovalKey((k) => k + 1); // force a fresh remount so the transition replays
                } else if (!nowInView) {
                    setApprovalDone(false);
                }

                approvalWasInViewRef.current = nowInView;
            },
            { threshold: 0.4, rootMargin: "0px 0px -10% 0px" }
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (approvalKey === 0) return; // don't autoplay on initial mount
        const t = setTimeout(() => setApprovalDone(true), 100);
        return () => clearTimeout(t);
    }, [approvalKey]);
    const toggleMenu = () => setIsMenuOpen((prev) => !prev);
    const closeMenu = () => setIsMenuOpen(false);

    const inr = (n: number) => "₹" + n.toLocaleString("en-IN");

    return (
        <div className="min-h-screen flex flex-col relative">
            {/* ================= HEADER + HERO ================= */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 -z-10">
                    <GreyGridBackground />
                </div>

                {/* ---------------- HEADER ---------------- */}
                <header className="relative z-50 py-6 bg-transparent">
                    <nav className="max-w-[1440px] mx-auto px-8">
                        <div className="flex items-center justify-between">
                            <div className="flex-shrink-0">
                                <img src={CleverPeLogo} alt="CleverPe" className="h-[32px] w-auto" />
                            </div>

                            <div className="md:hidden">
                                <button
                                    onClick={toggleMenu}
                                    className="text-gray-900 text-2xl focus:outline-none"
                                >
                                    {isMenuOpen ? <FaTimes /> : <FaBars />}
                                </button>
                            </div>

                            <div
                                className={`fixed inset-0 z-50 flex flex-col items-center justify-center space-y-6 bg-white transition-transform duration-300 ${isMenuOpen ? "translate-x-0" : "translate-x-full"
                                    } md:static md:flex md:flex-row md:space-y-0 md:space-x-14 md:translate-x-0 md:bg-transparent md:mx-auto`}
                            >
                                {isMenuOpen && (
                                    <button
                                        onClick={closeMenu}
                                        className="absolute top-4 right-4 text-gray-900 text-3xl md:hidden"
                                    >
                                        <FaTimes />
                                    </button>
                                )}

                                <a href="/" onClick={closeMenu} className="text-[16px] text-gray-700 hover:text-gray-900">Home</a>
                                <a href="/#featurescard" onClick={closeMenu} className="text-[16px] text-gray-700 hover:text-gray-900">Solution</a>
                                <a href="/#evfinancing" onClick={closeMenu} className="text-[16px] text-gray-700 hover:text-gray-900">Electric Vehicle</a>
                                <a href="/Insurance" onClick={closeMenu} className="text-[16px] text-gray-700 hover:text-gray-900">Insurance</a>
                                <a href="/Health" onClick={closeMenu} className="text-[16px] text-gray-700 hover:text-gray-900">Healthcare</a>
                                <a href="/aboutus" onClick={closeMenu} className="text-[16px] text-gray-700 hover:text-gray-900">About</a>
                            </div>
                        </div>
                    </nav>
                </header>

                {/* ---------------- HERO ---------------- */}
                <section className="relative py-16 md:py-20">
                    <div className="max-w-screen-xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
                        {/* LEFT */}
                        <div className="text-center md:text-left">
                            <h1 className="text-[36px] sm:text-[44px] md:text-[60px] font-bold leading-tight text-gray-900">
                                Treatment shouldn’t{" "}
                                <br className="hidden sm:block" />
                                wait for savings
                            </h1>

                            <p className="mt-6 text-base sm:text-lg text-gray-600 max-w-xl mx-auto md:mx-0">
                                CleverPe Healthcare Financing is an embedded checkout solution that allows
                                patients to finance high-value medical treatments through manageable EMIs
                                without upfront financial burden, paperwork, or delays.
                            </p>

                            <div className="mt-8 flex justify-center md:justify-start">
                                <button className="px-10 md:px-12 py-3 bg-gradient-purple text-white font-medium rounded-full">
                                    Enable Healthcare Financing
                                </button>
                            </div>
                        </div>

                        {/* RIGHT — live splitter */}
                        <div className="relative">
                            <div className="relative rounded-[28px] border border-gray-200 bg-white
                          shadow-[0_24px_60px_-28px_rgba(0,0,0,0.35)] p-6 sm:p-8">
                                {/* bill header */}
                                <div className="flex items-start justify-between">
                                    <div>
                                        <p className="text-xs uppercase tracking-[0.18em] text-gray-400">
                                            Hospital bill
                                        </p>
                                        <p className="mt-2 text-[34px] sm:text-[40px] font-bold leading-none text-gray-900">
                                            {inr(amount)}
                                        </p>
                                    </div>
                                </div>

                                {/* amount slider */}
                                <input
                                    type="range"
                                    min={20000}
                                    max={500000}
                                    step={5000}
                                    value={amount}
                                    onChange={(e) => setAmount(Number(e.target.value))}
                                    className="mt-5 w-full accent-black cursor-pointer"
                                    aria-label="Bill amount"
                                />
                                <div className="flex justify-between text-[11px] text-gray-400">
                                    <span>₹20K</span>
                                    <span>₹5L</span>
                                </div>

                                {/* the split line */}
                                <div className="my-7 flex items-center gap-3">
                                    <div className="h-px flex-1 bg-gray-200" />
                                    <span className="text-[11px] uppercase tracking-[0.18em] text-gray-400">
                                        splits into
                                    </span>
                                    <div className="h-px flex-1 bg-gray-200" />
                                </div>

                                {/* tenure chips */}
                                <div className="flex flex-wrap gap-2">
                                    {TENURES.map((t) => (
                                        <button
                                            key={t}
                                            onClick={() => setTenure(t)}
                                            className={`px-4 py-2 rounded-full text-sm border transition-all ${tenure === t
                                                ? "bg-gray-900 text-white border-gray-900"
                                                : "bg-white text-gray-600 border-gray-300 hover:border-gray-900"
                                                }`}
                                        >
                                            {t} mo
                                        </button>
                                    ))}
                                </div>

                                {/* emi result */}
                                <div className="mt-7 rounded-2xl bg-gray-900 text-white p-6">
                                    <p className="text-xs uppercase tracking-[0.18em] text-white/50">
                                        Patient pays
                                    </p>
                                    <p className="mt-2 text-[34px] sm:text-[40px] font-bold leading-none">
                                        {inr(emi)}
                                        <span className="text-base font-normal text-white/60"> /month</span>
                                    </p>

                                    {/* segmented bar showing the tenure */}
                                    <div className="mt-5 flex gap-1">
                                        {Array.from({ length: tenure }).map((_, i) => (
                                            <div key={i} className="h-1.5 flex-1 rounded-full bg-white/25" />
                                        ))}
                                    </div>

                                    <p className="mt-4 text-sm text-white/60">
                                        Hospital receives {inr(amount)} upfront, on day one.
                                    </p>
                                </div>

                                <p className="mt-4 text-[11px] text-gray-700 text-center">
                                    *Final tenure and charges depend on lender approval.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
            </section>

            <main className="flex-grow">


                {/* =========================================================
    SECTION — THE CLEVERPE JOURNEY
========================================================= */}
                <section
                    ref={sectionRef}
                    className="relative overflow-hidden -mt-10 md:-mt-16"
                >
                    <div className="max-w-screen-xl mx-auto px-6 pt-8 pb-20 md:pt-12 md:py-28">

                        {/* Header */}
                        <div className="text-center max-w-3xl mx-auto">
                            <h2 className="mt-5 text-3xl md:text-[42px] font-bold text-gray-900 leading-tight tracking-tight">
                                Your Healthcare Financing Journey,
                                <span className="block bg-gradient-purple bg-clip-text text-transparent">
                                    Simplified
                                </span>
                            </h2>

                            <p className="mt-5 text-base md:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
                                A completely digital financing journey designed to help patients
                                complete their loan process quickly and securely.
                            </p>
                        </div>

                        {/* Interactive journey */}
                        <div className="mt-16 md:mt-20 grid lg:grid-cols-[auto_450px] gap-4 justify-center items-center">

                            {/* =====================================================
                LEFT — SINGLE CSS PHONE FRAME
            ===================================================== */}
                            <div className="order-1 lg:order-1 flex justify-center">

                                <div className="relative h-[640px] w-[314px] flex items-center justify-center">

                                    {/* Soft glow behind phone */}
                                    <div
                                        className="
                            absolute
                            w-[360px]
                            h-[430px]
                            rounded-full
                            blur-3xl
                            opacity-70
                            bg-purple-100/70
                        "
                                    />

                                    {/* =================================================
                        PHONE
                    ================================================= */}
                                    <div
                                        className="
                            relative
                            z-10
                            h-[640px]
                            w-[314px]
                            rounded-[48px]
                            bg-[#08090b]
                            p-[7px]
                            shadow-[0_25px_55px_-15px_rgba(0,0,0,0.30)]
                        "
                                    >

                                        {/* Inner screen */}
                                        <div
                                            className="
        absolute
        inset-[7px]
        overflow-hidden
        rounded-[42px]
        bg-white
    "
                                        >
                                            {STEPS.map((step, i) => (
                                                <img
                                                    key={step.title}
                                                    src={step.img}
                                                    alt={step.title}
                                                    className={`
        absolute
        left-0
        top-0
        w-full
        h-full
        object-cover
        object-top
        transition-opacity
        duration-500
        ${i === active ? "opacity-100" : "opacity-0"}
    `}
                                                />
                                            ))}
                                        </div>

                                        {/* =================================================
                            PHONE SIDE BUTTONS
                        ================================================= */}

                                        {/* Power button */}
                                        <div
                                            className="
                                absolute
                                -right-[3px]
                                top-[150px]
                                w-[4px]
                                h-[76px]
                                rounded-r-md
                                bg-[#191a1d]
                            "
                                        />

                                        {/* Volume up */}
                                        <div
                                            className="
                                absolute
                                -left-[3px]
                                top-[145px]
                                w-[4px]
                                h-[30px]
                                rounded-l-md
                                bg-[#191a1d]
                            "
                                        />

                                        {/* Volume down */}
                                        <div
                                            className="
                                absolute
                                -left-[3px]
                                top-[187px]
                                w-[4px]
                                h-[55px]
                                rounded-l-md
                                bg-[#191a1d]
                            "
                                        />

                                    </div>
                                </div>
                            </div>


                            {/* =====================================================
                RIGHT — JOURNEY STEPPER
            ===================================================== */}
                            <div className="order-2 lg:order-2 lg:ml-14 flex flex-col gap-1.5">

                                {STEPS.map((step, i) => {

                                    const isActive =
                                        i === active &&
                                        !(hasCompleted && i === STEPS.length - 1);

                                    const isDone =
                                        i < active ||
                                        (hasCompleted && i === STEPS.length - 1);

                                    return (
                                        <button
                                            key={step.title}
                                            onClick={() => goTo(i)}
                                            className={`
                                group
                                text-left
                                rounded-2xl
                                px-5
                                py-4
                                cursor-pointer
                                transition-all
                                duration-300
                                border
                                ${isActive
                                                    ? "bg-white border-gray-200 shadow-[0_8px_24px_-12px_rgba(0,0,0,0.15)]"
                                                    : "bg-transparent border-transparent hover:bg-gray-50 hover:border-gray-100"
                                                }
                            `}
                                        >

                                            {/* Step header */}
                                            <div className="flex items-center gap-3">

                                                {/* Number / completed check */}
                                                <span
                                                    className={`
                                        relative
                                        flex
                                        items-center
                                        justify-center
                                        w-7
                                        h-7
                                        rounded-full
                                        text-xs
                                        font-bold
                                        shrink-0
                                        transition-all
                                        duration-300
                                        ${isDone
                                                            ? "bg-emerald-500 text-white scale-100"
                                                            : isActive
                                                                ? "bg-gradient-purple text-white scale-110 shadow-[0_4px_14px_-2px_rgba(124,58,237,0.5)]"
                                                                : "bg-gray-100 text-gray-400 group-hover:bg-gray-200"
                                                        }
                                    `}
                                                >
                                                    {isDone ? (
                                                        <svg
                                                            viewBox="0 0 24 24"
                                                            className="w-3.5 h-3.5"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            strokeWidth="2.5"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        >
                                                            <path d="m5 12.5 4.5 4.5L19 7.5" />
                                                        </svg>
                                                    ) : (
                                                        i + 1
                                                    )}
                                                </span>

                                                {/* Title */}
                                                <h3
                                                    className={`
                                        flex-1
                                        text-base
                                        font-semibold
                                        transition-colors
                                        duration-300
                                        ${isActive
                                                            ? "text-gray-900"
                                                            : isDone
                                                                ? "text-gray-500"
                                                                : "text-gray-400"
                                                        }
                                    `}
                                                >
                                                    {step.title}
                                                </h3>

                                                {/* Chevron */}
                                                <svg
                                                    viewBox="0 0 24 24"
                                                    className={`
                                        w-4
                                        h-4
                                        shrink-0
                                        transition-all
                                        duration-300
                                        ${isActive
                                                            ? "text-purple-500 translate-x-0.5"
                                                            : "text-gray-300 group-hover:text-gray-400 group-hover:translate-x-0.5"
                                                        }
                                    `}
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                >
                                                    <path d="M9 6l6 6-6 6" />
                                                </svg>

                                            </div>


                                            {/* Progress bar */}
                                            {!isDone && (
                                                <div
                                                    className="
                                        mt-3
                                        ml-10
                                        h-[3px]
                                        w-[calc(100%-2.5rem)]
                                        bg-gray-100
                                        rounded-full
                                        overflow-hidden
                                    "
                                                >
                                                    <div
                                                        className="
                                            h-full
                                            rounded-full
                                            bg-gradient-purple
                                        "
                                                        style={{
                                                            width: isActive
                                                                ? `${progress * 100}%`
                                                                : "0%",
                                                            transition: isActive
                                                                ? "none"
                                                                : "width 0.3s ease",
                                                        }}
                                                    />
                                                </div>
                                            )}


                                            {/* Description */}
                                            {isActive && (
                                                <p
                                                    className="
                                        mt-2.5
                                        ml-10
                                        text-sm
                                        text-gray-600
                                        leading-relaxed
                                    "
                                                >
                                                    {step.desc}
                                                </p>
                                            )}

                                        </button>
                                    );
                                })}

                            </div>

                        </div>
                    </div>
                </section>

                <section className="max-w-screen-xl mx-auto px-6 pt-8 pb-20 md:pt-12 md:pb-28 -mt-8 md:-mt-12">

                    {/* =========================================================
                        SECTION — WHY CLEVERPE
                    ========================================================= */}
                    <div>
                        <div className="max-w-2xl">
                            <h2 className="text-3xl md:text-[40px] font-bold text-gray-900 leading-tight">
                                Why CleverPe
                            </h2>

                            <p className="mt-3 text-lg text-gray-500 leading-relaxed">
                                Where care comes first, payment comes easy
                            </p>
                        </div>

                        <div className="mt-12 grid md:grid-cols-3 gap-6">

                            {/* Card 1 — Fast Approval */}
                            <div
                                ref={approvalCardRef}
                                className="flex flex-col items-center text-center relative overflow-hidden rounded-3xl bg-white p-7 min-h-[260px] border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-all duration-300"
                            >
                                <div className="w-11 h-11 rounded-2xl bg-gradient-purple flex items-center justify-center">
                                    <IconBolt className="w-5.5 h-5.5 text-white" />
                                </div>

                                <h3 className="mt-6 text-lg font-semibold text-gray-900">
                                    Fast, digital approval
                                </h3>

                                <p className="mt-2 text-sm text-gray-500 leading-relaxed">
                                    Decisions at the front desk, not after days of waiting.
                                </p>

                                {/* Animated approval tracker */}
                                <div className="absolute bottom-7 left-7 right-7">
                                    <div className="relative">
                                        {/* Track */}
                                        <div className="absolute top-3 left-3 right-3 h-[2px] bg-gray-200" />

                                        {/* Animated progress line */}
                                        <div
                                            className={`absolute top-3 left-3 h-[2px] bg-gradient-purple transition-all duration-[2200ms] ease-in-out ${approvalDone ? "w-[100%]" : "w-0"
                                                }`}
                                        />

                                        <div className="relative flex justify-between">
                                            {/* Applied */}
                                            <div className="flex flex-col items-start">
                                                <div className="w-6 h-6 rounded-full bg-indigo-500 border-4 border-white shadow-sm flex items-center justify-center transition-all duration-300">
                                                    <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 12l4 4L19 6" />
                                                    </svg>
                                                </div>
                                                <span className="mt-2 text-[10px] font-medium text-gray-500">
                                                    Applied
                                                </span>
                                            </div>

                                            {/* Reviewing */}
                                            <div className="flex flex-col items-center">
                                                <div
                                                    className={`relative w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-500 ${approvalDone
                                                        ? "bg-indigo-500 border-indigo-100"
                                                        : "bg-gray-100 border-gray-200"
                                                        }`}
                                                >
                                                    <span
                                                        className={`w-2 h-2 rounded-full transition-all duration-500 ${approvalDone ? "bg-white animate-pulse" : "bg-gray-300"
                                                            }`}
                                                    />
                                                </div>
                                                <span
                                                    className={`mt-2 text-[10px] transition-all duration-500 ${approvalDone ? "text-indigo-500 font-semibold" : "text-gray-400"
                                                        }`}
                                                >
                                                    Reviewing
                                                </span>
                                            </div>

                                            {/* Approved */}
                                            <div className="flex flex-col items-end">
                                                <div
                                                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-500 ${approvalDone
                                                        ? "bg-emerald-500 border-emerald-500 scale-110"
                                                        : "bg-white border-gray-200"
                                                        }`}
                                                >
                                                    <svg
                                                        className={`w-3.5 h-3.5 text-white transition-all duration-300 delay-500 ${approvalDone ? "opacity-100 scale-100" : "opacity-0 scale-50"
                                                            }`}
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="3"
                                                    >
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 12l4 4L19 6" />
                                                    </svg>
                                                </div>
                                                <span
                                                    className={`mt-2 text-[10px] transition-all duration-500 ${approvalDone ? "text-emerald-500 font-semibold" : "text-gray-400"
                                                        }`}
                                                >
                                                    Approved
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Card 2 — Flexible EMI */}
                            <div className="group flex flex-col items-center text-center relative overflow-hidden rounded-3xl bg-white p-7 min-h-[260px] border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-all duration-300">

                                <div className="w-11 h-11 rounded-2xl bg-gradient-purple flex items-center justify-center">
                                    <IconReceipt className="w-5.5 h-5.5 text-white" />
                                </div>

                                <h3 className="mt-6 text-lg font-semibold text-gray-900">
                                    Transparent pricing, flexible tenures
                                </h3>

                                <p className="mt-2 text-sm text-gray-500 leading-relaxed">
                                    Clear pricing with EMI options that fit customer needs.
                                </p>

                                {/* Interactive tenure selector */}
                                <div className="absolute bottom-7 left-7 right-7 flex gap-2">
                                    <div
                                        className="
                flex-1 rounded-xl
                border border-gray-100 bg-gray-50
                py-2.5 text-center
                text-[11px] text-gray-400
                cursor-pointer
                transition-all duration-300
                hover:border-indigo-200
                hover:bg-indigo-50
                hover:text-indigo-500
                hover:font-semibold
                hover:-translate-y-0.5
            "
                                    >
                                        3 Months
                                    </div>

                                    <div
                                        className="
                flex-1 rounded-xl
                border border-gray-100 bg-gray-50
                py-2.5 text-center
                text-[11px] text-gray-400
                cursor-pointer
                transition-all duration-300
                hover:border-indigo-200
                hover:bg-indigo-50
                hover:text-indigo-500
                hover:font-semibold
                hover:-translate-y-0.5
            "
                                    >
                                        6 Months
                                    </div>

                                    <div
                                        className="
                flex-1 rounded-xl
                border border-gray-100 bg-gray-50
                py-2.5 text-center
                text-[11px] text-gray-400
                cursor-pointer
                transition-all duration-300
                hover:border-indigo-200
                hover:bg-indigo-50
                hover:text-indigo-500
                hover:font-semibold
                hover:-translate-y-0.5
            "
                                    >
                                        9 Months
                                    </div>

                                    <div
                                        className="
                flex-1 rounded-xl
                border border-gray-100 bg-gray-50
                py-2.5 text-center
                text-[11px] text-gray-400
                cursor-pointer
                transition-all duration-300
                hover:border-indigo-200
                hover:bg-indigo-50
                hover:text-indigo-500
                hover:font-semibold
                hover:-translate-y-0.5
            "
                                    >
                                        12 Months
                                    </div>
                                </div>
                            </div>

                            {/* Card 3 — Trusted Partners */}
                            <div className="group flex flex-col items-center text-center relative overflow-hidden rounded-3xl bg-white p-7 min-h-[260px] border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-all duration-300">

                                <div className="w-11 h-11 rounded-2xl bg-gradient-purple flex items-center justify-center">
                                    <IconWorkflow className="w-5.5 h-5.5 text-white" />
                                </div>

                                <h3 className="mt-6 text-lg font-semibold text-gray-900">
                                    Built around your workflow
                                </h3>

                                <p className="mt-2 text-sm text-gray-500 leading-relaxed">
                                    CleverPe integrates into your existing processes without adding operational complexity.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* =====================================================
                        PARTNER HOSPITALS / CLINICS
                    ===================================================== */}
                    <div className="mt-24 rounded-[32px] border border-gray-200 bg-gray-50 px-6 py-14 md:py-16">

                        <div className="text-center max-w-xl mx-auto">
                            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                                Our Healthcare Partners
                            </p>

                            <h3 className="mt-3 text-2xl md:text-3xl font-bold text-gray-900">
                                Trusted by leading hospitals and clinics
                            </h3>
                        </div>

                        <div className="mt-10 flex flex-wrap items-center justify-center gap-6">
                            {[partnerLogo1, partnerLogo2, partnerLogo3].map((logo, i) => (
                                <div
                                    key={i}
                                    className="w-56 h-32 md:w-64 md:h-36 rounded-2xl border border-gray-200 bg-white
                                shadow-sm flex items-center justify-center p-8 transition-all
                                hover:border-gray-300 hover:shadow-md hover:-translate-y-1"
                                >
                                    <img
                                        src={logo}
                                        alt="CleverPe healthcare partner logo"
                                        className="max-h-full max-w-full w-auto h-auto object-contain"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            </main>

            <Footer bgColor="bg-[#2F2E2E]" />
        </div>
    );
};

export default HealthFinancing;